/**
 * Maintenance screen — rendered in place of the whole app when the host has
 * answered /v1 with 503.
 *
 * IT EXPLAINS, IT DOES NOT ENFORCE. The gate is Caddy on the host; see
 * `lib/maintenance.ts`. Removing this screen would not let anyone transact.
 *
 * RECOVERY IS A RELOAD, on purpose. There is no "am I back up yet" endpoint to
 * poll: the gate covers /v1/* and deliberately leaves /health open (the load
 * balancer probes it), so polling /health would report "back up" while every
 * real request is still refused. Reloading re-boots the app and lets its normal
 * requests answer the question honestly.
 *
 * Rendered outside the router, so it does not depend on the route tree having
 * mounted — during a window the very first boot request is the one that 503s.
 */

import { useEffect, useLayoutEffect, useRef } from 'react';

import { useLocale, useT } from '@/i18n';

export default function Maintenance() {
  const t = useT();
  const { locale } = useLocale();
  const titleRef = useRef<HTMLHeadingElement>(null);

  // The underlying route stays mounted conceptually even though maintenance
  // replaces its UI. In particular, /welcome normally forces English at the
  // document level, so align the language metadata with the translated outage
  // copy before it is painted.
  useLayoutEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  // A 503 can replace the shell while focus is inside a control that is about
  // to unmount. Move focus to the explanation so keyboard and screen-reader
  // users are told why the interface disappeared.
  useEffect(() => {
    titleRef.current?.focus();
  }, []);

  return (
    <main className="maintenance" data-region="main" role="main">
      {/* One thought: what is happening. Grouped so its internal rhythm is
          tighter than the gap to the reassurance and the action below. */}
      <div className="maintenance__message">
        <span className="maintenance__mark" aria-hidden="true">
          {/* A wrench, not a warning triangle: planned work, not a fault. */}
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"
               strokeLinecap="round" strokeLinejoin="round">
            <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
          </svg>
        </span>

        <h1 ref={titleRef} className="maintenance__title" tabIndex={-1}>
          {t('maintenance.title')}
        </h1>
        <p className="maintenance__body">{t('maintenance.body')}</p>
      </div>

      {/* Set apart deliberately. This is the question an anxious person opens
          the app to answer, so it gets its own ground rather than being the
          smallest line on the screen. */}
      <p className="maintenance__assurance">{t('maintenance.assurance')}</p>

      <button
        type="button"
        className="maintenance__retry"
        onClick={() => window.location.reload()}
      >
        {t('maintenance.retry')}
      </button>
    </main>
  );
}
